import React, { useState, useEffect } from "react";
import {
  Card,
  Text,
  Group,
  Stack,
  Avatar,
  ActionIcon,
  Box,
  Title,
  Button,
} from "@mantine/core";
import { IconEdit, IconPlus, IconGripVertical } from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import EditRepTypeModal from "./EditRepTypeModal";

// Sortable Item Component
const SortableRepTypeItem = ({ repType, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: repType.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} mb="sm">
      <Box
        style={{
          padding: ".5rem",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#fafafa",
          cursor: isDragging ? "grabbing" : "default",
        }}
      >
        <Group justify="space-between" align="center" w="100%">
          <ActionIcon
            variant="subtle"
            size="md"
            {...listeners}
            style={{ cursor: "grab" }}
            aria-label="Drag to reorder"
          >
            <IconGripVertical size={20} />
          </ActionIcon>

          <Avatar
            src={repType.image}
            size="lg"
            radius="xl"
            alt={repType.name}
            style={{ border: "2px solid #e0e0e0" }}
          >
            {repType.name?.charAt(0)?.toUpperCase() || "R"}
          </Avatar>

          <Text
            size="lg"
            style={{ flex: 1, textAlign: "center" }}
            lineClamp={1}
          >
            {repType.name}
          </Text>

          <ActionIcon
            variant="subtle"
            size="md"
            onClick={() => onEdit(repType)}
            aria-label={`Edit ${repType.name}`}
          >
            <IconEdit size={20} />
          </ActionIcon>
        </Group>
      </Box>
    </Box>
  );
};

const RepTypesCard = () => {
  const { getVerifiedJWT } = useAuth();
  const [repTypes, setRepTypes] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedRepType, setSelectedRepType] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder items and call API
  const handleDragEnd = async event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = repTypes.findIndex(item => item.id === active.id);
      const newIndex = repTypes.findIndex(item => item.id === over.id);

      const newRepTypes = arrayMove(repTypes, oldIndex, newIndex);
      setRepTypes(newRepTypes);

      // Call API to update sort order
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) {
          return;
        }

        const movedRepType = newRepTypes[newIndex];
        const response = await fetch(
          getApiEndpoint("/v1/company/rep-type/sort"),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              rep_type_id: movedRepType.id,
              sort_order: newIndex + 1, // 1-based index for sort order
            }),
          }
        );

        if (!response.ok) {
          setRepTypes(repTypes);
        }
      } catch (err) {
        setRepTypes(repTypes);
      }
    }
  };

  // Fetch rep types from API
  const fetchRepTypes = async () => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        return;
      }

      const response = await fetch(getApiEndpoint("/v2/company/rep-types"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || [];
        setRepTypes(data);
      }
    } catch (err) {}
  };

  // Handle updating a rep type
  const handleUpdateRepType = async updatedRepType => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      // Create FormData for file upload (similar to add functionality)
      const formData = new FormData();
      formData.append("name", updatedRepType.name);

      // Only append image if a new one was selected
      if (updatedRepType.image) {
        formData.append("image", updatedRepType.image);
      }

      const response = await fetch(
        getApiEndpoint(`/v1/company/rep-type/${updatedRepType.id}`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${jwt}`,
            // Don't set Content-Type header - let browser set it with boundary for FormData
          },
          body: formData,
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Update the local state with the response from API
        const updatedTypes = repTypes.map(type =>
          type.id === updatedRepType.id ? result.data || result : type
        );
        setRepTypes(updatedTypes);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update rep type");
      }
    } catch (err) {
      throw new Error(err.message || "Failed to update rep type");
    }
  };

  // Handle adding a new rep type
  const handleAddRepType = async newRepType => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", newRepType.name);

      if (newRepType.image) {
        formData.append("image", newRepType.image);
      }

      const response = await fetch(getApiEndpoint("/v2/company/rep-type"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          // Don't set Content-Type header - let browser set it with boundary for FormData
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        // Add the new rep type returned from API to the local state
        setRepTypes(prev => [...prev, result.data || result]);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create rep type");
      }
    } catch (err) {
      throw err; // Re-throw to be handled by the modal
    }
  };

  // Handle deleting a rep type
  const handleDeleteRepType = async repTypeId => {
    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v2/company/rep-type/${repTypeId}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        // Remove the deleted rep type from local state
        setRepTypes(prev => prev.filter(repType => repType.id !== repTypeId));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete rep type");
      }
    } catch (err) {
      throw new Error(err.message || "Failed to delete rep type");
    }
  };

  const handleEditClick = repType => {
    setSelectedRepType(repType);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedRepType(null);
  };

  const handleSave = async updatedRepType => {
    try {
      await handleUpdateRepType(updatedRepType);
      handleModalClose();
    } catch (error) {
      throw error;
    }
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
  };

  const handleAddSave = async newRepType => {
    try {
      await handleAddRepType(newRepType);
      handleAddModalClose();
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    fetchRepTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Rep Types</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setAddModalOpen(true)}
            variant="filled"
            size="sm"
          >
            Add Rep Type
          </Button>
        </Group>

        {!repTypes || repTypes.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No rep types configured yet
          </Text>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={repTypes}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap="sm">
                {repTypes.map(repType => (
                  <SortableRepTypeItem
                    key={repType.id}
                    repType={repType}
                    onEdit={handleEditClick}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      <EditRepTypeModal
        opened={editModalOpen}
        onClose={handleModalClose}
        repType={selectedRepType}
        onSave={handleSave}
        onDelete={handleDeleteRepType}
      />

      <EditRepTypeModal
        opened={addModalOpen}
        onClose={handleAddModalClose}
        repType={null}
        onSave={handleAddSave}
      />
    </>
  );
};

export default RepTypesCard;
